import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '../../utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'
import { transformToFormData } from '@/utils/formData'

export const documentFilters = {
   document_legal: { label: "Document légal", value: 'document_legal', key: 'type_document'},
   decompte: { label: "Décompte", value: 'decompte', key: 'type_document'},
   facture: { label: "Facture", value: 'facture', key: 'type_document'},
   ancienne_quittance: { label: "Ancienne quittance", value: 'ancienne_quittance', key: 'type_document'}
}
const documentFilterIds = [
  "document_legal",
  "decompte",
  "facture",
  "ancienne_quittance"
]

export const documentTypeOptions = documentFilterIds.map( key => ({
  label: documentFilters[key].label,
  value: documentFilters[key].value
}))

export const documentTypes = documentFilterIds.reduce( (acc, key) => ({
  ...acc,
  [key]: documentFilters[key].label
}),
{}
)

export const ordonnedDocumentFilters = (currentFilters) => documentFilterIds.map(
  key => ({
    ...documentFilters[key],
    checked: currentFilters[documentFilters[key].key] === documentFilters[key].value
  })
)

const URL = 'documents'
const getUrl = (id) => {
  if(id) {
    return `${URL}/${id}`
  }
  return URL
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 100
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
  // pour Extranet Proprietaires
  decompte: {
    items: [],
    pagination: {
      totalCount: 0,
      totalPages: 0,
      currentPage: 1
    }
  },
  document_legal: {
    items: [],
    pagination: {
      totalCount: 0,
      totalPages: 0,
      currentPage: 1
    }
  },
}

const sanitizeDocumentFormData = (data) => {
  const sanitizedData = sanitizeFormData(data)

  // Traiter le bien
  sanitizedData.bien_id = sanitizedData.bien?.id || null
  delete sanitizedData.bien

  // Traiter les contacts
  if (sanitizedData.contacts && Array.isArray(sanitizedData.contacts)) {
    sanitizedData.contacts = sanitizedData.contacts
      .filter(item => item.id !== undefined)
      .map(item => ({
        contact_id: item.id
      }))
  }

  // Vérifier s'il y a des fichiers à uploader
  const needToSendFormData = Object.keys(sanitizedData).some(key =>
    sanitizedData[key] instanceof File ||
    (Array.isArray(sanitizedData[key]) && sanitizedData[key].some(item => item instanceof File))
  )

  let formData
  if (needToSendFormData) {
    formData = transformToFormData(sanitizedData, 'document')
  } else {
    formData = { document: sanitizedData }
  }
  return formData
}

// Thunks
export const fetchDocuments = createApiAsyncThunk(
  'documents/fetchDocuments',
  async (params = {}, { api, dispatch, rejectWithValue }) => {
    // TODO si params comprend un term (aka recherche)
    //      faire un dispatch searchDocuments()
    try {
      let url = URL
      if(params.term) {
        url = `${url}/search`
      }
      const response = await api
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des documents')
    }
  }
)

// Utiliser par l'extranet pour récupérer les documents liés au bien affiché
export const fetchBienDocuments = createApiAsyncThunk(
  'documents/fetchBienDocuments',
  async (params = {}, { api, dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      if(!params.bienId) { return rejectWithValue() }

      let url = `/biens/${params.bienId}/documents`
      delete params.bienId

      await setTimeoutInDev()
      const response = await api
        .url(url)
        .query(params)
        .get()
      
      dispatch(setDocumentByTypeList({...response, type_document: "document_legal"}))
      return fulfillWithValue()
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des documents')
    }
  }
)

export const createDocument = createApiAsyncThunk(
  'documents/createDocument',
  async (data , { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl())
        .post(sanitizeDocumentFormData(data))
      if(data.type_document === "ancienne_quittance") {
        dispatch(addFlashMessage({ type: (response.success ? 'success' : 'error'), text: response.message }))
      } else {
        dispatch(addFlashMessage({type: 'success', text: "Document créé avec succès"}))
      }
      return response

    } catch (error) {
      console.error(error)
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateDocument = createApiAsyncThunk(
  'documents/updateDocument',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(id))
        .put(sanitizeDocumentFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Document mis à jour avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteDocument = createApiAsyncThunk(
  'documents/deleteDocument',
  async (document, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(document.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Le document a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour un document vide
export const emptyDocument = {
  bien_id: null,
  titre: '',
  type_document: '',
  date_disponibilite: null,
  display_to_proprietaire: false,
  display_to_locataire: false,
  display_to_acquereur: false,
  file: null
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyDocument[key] || "") : value]
  )
)

// Slice
const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocumentByTypeList: (state, action) => {
      if(action.payload.type_document) {
        state[action.payload.type_document] = {
          items: action.payload.documents,
          pagination: {
            totalCount: action.payload.total_count,
            totalPages: action.payload.total_pages,
            currentPage: action.payload.current_page
          }
        }
      }
    },
    setEmptyCurrentDocument: (state, action) => {
      const data = {
        ...emptyDocument,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentDocument: (state, action) => {
      const sanitizedData = {
        ...emptyDocument,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentDocument: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setDocumentInEdition: (state, action) => {
      state.inEdition = {
        ...emptyDocument,
        ...action.payload
      }
      state.requestStatus = initialState.requestStatus
    },
    resetDocumentInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    }
  },
  extraReducers: (builder) => {
    // fetchDocuments
    builder.addCase(fetchDocuments.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchDocuments.fulfilled, (state, action) => {
      state.items = action.payload.documents
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchDocuments.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })
    
    // fetchBiensDocuments
    builder.addCase(fetchBienDocuments.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchBienDocuments.fulfilled, (state) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchBienDocuments.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createDocument
    builder.addCase(createDocument.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createDocument.fulfilled, (state, action) => {
      if(action.meta.arg.type_document !== "ancienne_quittance") {
        state.items.unshift(action.payload)
      }
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createDocument.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateDocument
    builder.addCase(updateDocument.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateDocument.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateDocument.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Document
    builder.addCase(deleteDocument.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteDocument.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteDocument.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const {
  setDocumentByTypeList,
  setEmptyCurrentDocument,
  setCurrentDocument,
  resetCurrentDocument,
  setDocumentInEdition,
  resetDocumentInEdition,
  setFilters,
  resetRequestStatus
} = documentsSlice.actions

export default documentsSlice.reducer